import { Injectable, inject } from '@angular/core';
import { Observable, of } from 'rxjs';
import { map, switchMap, take } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { Actions, ofType } from '@ngrx/effects';
import { GrandCompte } from '../models/user.model';
import { Currency } from '../models';
import { Invoice, Receipt } from '../models/wallet.model';
import { WalletApiService } from './api/wallet-api.service';
import { CurrencyIso, toCurrencyIso } from '../utils/currency.utils';
import { GrandCompteDnsService } from './grand-compte-dns.service';
import * as WalletActions from '@shared/store/wallets/wallet.actions';
import * as WalletSelectors from '@shared/store/wallets/wallet.selectors';
import { WalletData, FacturationData } from '@shared/store/wallets/wallet.reducer';

@Injectable({
    providedIn: 'root',
})
export class WalletService {
    private store = inject(Store);
    private actions$ = inject(Actions);
    private grandCompteDnsService = inject(GrandCompteDnsService);
    private walletApiService = inject(WalletApiService);

    wallets$ = this.store.select(WalletSelectors.selectAllWallets);
    currentWallet$ = this.store.select(WalletSelectors.selectCurrentWallet);
    facturations$ = this.store.select(WalletSelectors.selectAllFacturations);
    currentFacturation$ = this.store.select(WalletSelectors.selectCurrentFacturation);

    get currentWallet(): WalletData | null {
        let current: WalletData | null = null;
        this.currentWallet$.pipe(take(1)).subscribe((wallet) => (current = wallet));
        return current;
    }

    get currentFacturation(): FacturationData | null {
        let current: FacturationData | null = null;
        this.currentFacturation$.pipe(take(1)).subscribe((facturation) => (current = facturation));
        return current;
    }

    get walletsSubjectValue(): { value: Map<string, WalletData> } {
        let walletsMap: Map<string, WalletData> = new Map();
        this.store
            .select(WalletSelectors.selectWalletsAsMap)
            .pipe(take(1))
            .subscribe((map) => (walletsMap = map));
        return { value: walletsMap };
    }

    get facturationsSubjectValue(): { value: Map<string, FacturationData> } {
        let facturationsMap: Map<string, FacturationData> = new Map();
        this.store
            .select(WalletSelectors.selectFacturationsAsMap)
            .pipe(take(1))
            .subscribe((map) => (facturationsMap = map));
        return { value: facturationsMap };
    }

    /**
     * Load wallets and facturations for a grand compte
     */
    loadWalletsAndFacturationsForGrandCompte(grandCompte: GrandCompte, curr?: Currency): Observable<{ wallets: any[]; facturations: any[] }> {
        if (!grandCompte?.advertisers?.length) {
            return of({ wallets: [], facturations: [] });
        }

        const currency = toCurrencyIso(curr ?? this.grandCompteDnsService.currentGCCurrency());
        const clientIds = grandCompte.advertisers.map((a) => a.client_id);

        // Dispatch actions to load both
        this.store.dispatch(
            WalletActions.loadWallets({
                clientIds,
                grandCompteId: grandCompte.id,
                currency,
            }),
        );

        this.store.dispatch(
            WalletActions.loadFacturations({
                clientIds,
                grandCompteId: grandCompte.id,
            }),
        );

        return this.store.select(WalletSelectors.selectWalletState).pipe(
            take(1),
            map((state) => ({
                wallets: Object.values(state.walletsMap),
                facturations: Object.values(state.facturationsMap),
            })),
        );
    }

    /**
     * Load only wallets for a grand compte
     */
    loadWalletsForGrandCompte(grandCompte: GrandCompte, curr?: Currency): Observable<any[]> {
        if (!grandCompte?.advertisers?.length) {
            return of([]);
        }

        const currency = toCurrencyIso(curr ?? this.grandCompteDnsService.currentGCCurrency());
        const clientIds = grandCompte.advertisers.map((a) => a.client_id);

        this.store.dispatch(
            WalletActions.loadWallets({
                clientIds,
                grandCompteId: grandCompte.id,
                currency,
            }),
        );

        return this.store.select(WalletSelectors.selectWalletsByGrandCompte(grandCompte.id));
    }

    /**
     * Load facturations for a grand compte
     */
    loadFacturationsForGrandCompte(grandCompte: GrandCompte): Observable<any[]> {
        if (!grandCompte?.advertisers?.length) {
            return of([]);
        }

        const clientIds = grandCompte.advertisers.map((a) => a.client_id);

        this.store.dispatch(
            WalletActions.loadFacturations({
                clientIds,
                grandCompteId: grandCompte.id,
            }),
        );

        return this.store.select(WalletSelectors.selectFacturationsByGrandCompte(grandCompte.id));
    }

    /**
     * Load a specific wallet
     */
    loadWallet(clientId: string, grandCompteId: number, curr?: Currency): Observable<WalletData | null> {
        const currency = toCurrencyIso(curr ?? this.grandCompteDnsService.currentGCCurrency());

        this.store.dispatch(
            WalletActions.loadWallets({
                clientIds: [clientId],
                grandCompteId,
                currency,
            }),
        );

        return this.store.select(WalletSelectors.selectWalletByClientAndGrandCompte(clientId, grandCompteId)).pipe(
            take(1),
            map((walletData) => {
                return walletData || null;
            }),
        );
    }

    /**
     * Load a specific facturation
     */
    loadFacturation(clientId: string, grandCompteId: number): Observable<any> {
        this.store.dispatch(
            WalletActions.loadFacturations({
                clientIds: [clientId],
                grandCompteId,
            }),
        );

        return this.store
            .select(WalletSelectors.selectFacturationByClientAndGrandCompte(clientId, grandCompteId))
            .pipe(map((facturationData) => facturationData?.facturation || null));
    }

    /**
     * Load facturation for current advertiser
     */
    loadFacturationForCurrentAdvertiser(): Observable<any> {
        const currentWallet = this.currentWallet;
        if (!currentWallet) {
            console.warn('No current wallet set.');
            return of(null);
        }

        return this.loadFacturation(currentWallet.clientId, currentWallet.grandCompteId);
    }

    /**
     * Set current wallet
     */
    setCurrentWallet(clientId: string, grandCompteId: number): void {
        this.store.dispatch(WalletActions.setCurrentWallet({ clientId, grandCompteId }));
    }

    /**
     * Get wallet by client and grand compte
     */
    getWallet(clientId: string, grandCompteId: number): WalletData | undefined {
        let wallet: WalletData | undefined = undefined;
        this.store
            .select(WalletSelectors.selectWalletByClientAndGrandCompte(clientId, grandCompteId))
            .pipe(take(1))
            .subscribe((w) => (wallet = w || undefined));
        return wallet;
    }

    /**
     * Get facturation by client and grand compte
     */
    getFacturation(clientId: string, grandCompteId: number): FacturationData | undefined {
        let facturation: FacturationData | undefined = undefined;
        this.store
            .select(WalletSelectors.selectFacturationByClientAndGrandCompte(clientId, grandCompteId))
            .pipe(take(1))
            .subscribe((f) => (facturation = f || undefined));
        return facturation;
    }

    /**
     * Balance of one wallet converted by the API into `currency`, straight from the API: the store is left untouched
     * (it stays in the global currency).
     */
    getWalletBalance(clientId: string, walletId: string, currency: CurrencyIso): Observable<number | null> {
        return this.walletApiService.getWalletBalance(clientId, walletId, currency);
    }

    /**
     * Clear all wallets and facturations
     */
    clearAllWallets(): void {
        this.store.dispatch(WalletActions.clearWalletsAndFacturations());
    }

    /**
     * Check and reload data if needed
     */
    /**
     * Facturation of one advertiser. Uses the store entry when present; otherwise loads it and waits for the answer,
     * so callers never mistake "not loaded yet" (e.g. right after a page refresh) for "incomplete". A failed load resolves to `null`.
     */
    getFacturationForAdvertiser(clientId: string, grandCompteId: number): Observable<FacturationData | null> {
        return this.store.select(WalletSelectors.selectAllFacturations).pipe(
            take(1),
            switchMap((facturations) => {
                const existing = facturations.find((f) => f.clientId === clientId && f.grandCompteId === grandCompteId);
                if (existing) return of(existing);

                const answer$ = this.actions$.pipe(
                    ofType(WalletActions.loadFacturationsSuccess, WalletActions.loadFacturationsFailure),
                    take(1),
                    map((action) =>
                        'facturations' in action
                            ? (action.facturations.find((f) => f.clientId === clientId && f.grandCompteId === grandCompteId) ?? null)
                            : null,
                    ),
                );
                // Subscribe before dispatching so a synchronous answer isn't missed
                return new Observable<FacturationData | null>((subscriber) => {
                    const subscription = answer$.subscribe(subscriber);
                    this.store.dispatch(WalletActions.loadFacturations({ clientIds: [clientId], grandCompteId }));
                    return subscription;
                });
            }),
        );
    }

    checkAndReloadDataIfNeeded(grandCompte: GrandCompte | null): void {
        if (!grandCompte) return;

        this.store
            .select(WalletSelectors.selectWalletState)
            .pipe(take(1))
            .subscribe((state) => {
                const walletsCount = Object.keys(state.walletsMap).length;
                const facturationsCount = Object.keys(state.facturationsMap).length;

                if (walletsCount === 0 && facturationsCount === 0 && grandCompte.advertisers?.length > 0) {
                    this.loadWalletsAndFacturationsForGrandCompte(grandCompte).subscribe();
                }
            });
    }

    getInvoices(clientId: string): Observable<any> {
        return this.walletApiService.getInvoices(clientId).pipe(
            map((response) => {
                return response;
            }),
        );
    }

    downloadInvoicePdf(
        source: Invoice['source'] | Receipt['source'],
        clientId: string,
        factureName: string,
        factureNumber: string,
    ): Observable<Blob> {
        return this.walletApiService.downloadInvoicePdf(source, clientId, factureName, factureNumber);
    }
}
